import { FunctionComponent, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from 'components/UI/Spinner';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import { SelectionBar } from 'components/UI/V3/SelectionBar/SelectionBar';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faSliders } from '@fortawesome/pro-regular-svg-icons';
import LeadsTable from './List/LeadsTable';
import LeadForm from './Form/LeadForm';
import styles from './LeadsComponent.module.scss';
import Button from 'components/UI/V3/Button/Button';
import LeadFiltersDrawer from './Filter/LeadFiltersDrawer';
import CreateBookingDrawer from 'components/UI/CreateBookingDrawer';
import TwoWaySMSDrawer from 'components/UI/TwoWaySMSDrawer/TwoWaySMSDrawer';
import ShareSendPanel from 'components/Forms/ShareSendPanel';
import { LeadsProvider, useLeadsData, useLeadsActions, useLeadsUI } from './LeadsProvider';
import { createPortal } from 'react-dom';
import { ConfirmDialog, OverFullWindow } from 'components/UI';
import Client from 'pages/Client';
import { Pet as PetModel } from '@spike/pet-model';
import { useHasPremiumPlan } from 'hooks/premiumPlan';
import PremiumFeatureAlert from 'components/UI/PremiumFeatureAlert';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { MarketplaceStatus, saveMarketplaceThunk } from '@spike/marketplace-action';
import { setTimeZone } from 'actions/login/LoginActions';
import { RootState } from 'store';
import { SETTINGS_FALSE_VALUE, SETTINGS_TRUE_VALUE } from '@spike/marketplace-model';
import { Switch } from 'components/UI/V3/Forms/Switch/Switch';
import LeadsActivation from './Activation/LeadsActivation';
import LeadDetail from './Detail/LeadDetail';

// ─── Inner content component (consumes context) ───────────────────────────────

const LeadsContent: FunctionComponent = () => {
    const {
        isFirstRender,
        totalRecords,
        stageCounts,
        selectedStage,
        handleStageChange,
        searchText,
        searchInputKey,
        handleSearchChange,
        appliedFiltersCount,
        hasActiveFilters,
        isGlobalEmpty
    } = useLeadsData();

    const { openAddForm, saveLead, isSaving, refreshLeads, fetchStagesCount, convertLead, isConverting } =
        useLeadsActions();

    const {
        showAddLead,
        editingLead,
        closeForm,
        showFilterLead,
        openFilterDrawer,
        closeFilterDrawer,
        showNewBooking,
        closeBooking,
        updateBookingPet,
        smsClient,
        closeSms,
        showShareSendPanel,
        shareSendLead,
        closeShareSendPanel,
        convertingLead,
        closeConvert,
        detailLead
    } = useLeadsUI();

    const { closeDetail } = useLeadsActions();

    const hasPremiumPlan = useHasPremiumPlan();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    const leadsEnabled = marketplace.settings?.find(s => s.key === 'leads_enabled')?.value === SETTINGS_TRUE_VALUE;
    const isSavingMarketplace = marketplaceStatus === MarketplaceStatus.Saving;

    const [showPremiumAlert, setShowPremiumAlert] = useState(false);
    const [addPetForClientId, setAddPetForClientId] = useState<number | undefined>(undefined);

    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    const handleAddPet = (clientId: number) => {
        setAddPetForClientId(clientId);
    };

    const handlePetSaved = (pet: PetModel) => {
        if (pet.id) {
            updateBookingPet(pet.id);
        }
        setAddPetForClientId(undefined);
    };

    const handleCloseAddPet = () => {
        setAddPetForClientId(undefined);
    };

    const handleBooked = () => {
        refreshLeads();
        fetchStagesCount();
        closeBooking();
    };

    const handleCloseSms = () => {
        refreshLeads();
        fetchStagesCount();
        closeSms();
    };

    const totalLeads =
        stageCounts.new + stageCounts.contacted + stageCounts.booked + stageCounts.won + stageCounts.lost;

    const stageOptions = useMemo(
        () => [
            { id: 'new', name: `New (${stageCounts.new})` },
            { id: 'contacted', name: `Contacted (${stageCounts.contacted})` },
            { id: 'booked', name: `Booked (${stageCounts.booked})` },
            { id: 'won', name: `Won (${stageCounts.won})` },
            { id: 'lost', name: `Lost (${stageCounts.lost})` },
            { id: 'all', name: `All (${totalLeads})` }
        ],
        [stageCounts]
    );

    const displayedLeadsCount = hasActiveFilters
        ? totalRecords
        : selectedStage === 'all'
        ? totalLeads
        : stageCounts[selectedStage] ?? 0;

    const percentageLeads = () => {
        if (hasActiveFilters) {
            if (totalLeads === 0) return 100;
            return ((totalRecords / totalLeads) * 100).toFixed(0);
        }
        if (selectedStage === 'all') return 100;
        return ((stageCounts[selectedStage] / totalLeads) * 100).toFixed(0);
    };

    const filterButtonLabel = isMobile
        ? ''
        : appliedFiltersCount > 0
        ? `Filter Leads (${appliedFiltersCount})`
        : 'Filter Leads';

    const renderFilterButton = () => {
        if (selectedStage === 'all') {
            return (
                <Button
                    label={filterButtonLabel}
                    variant="outlined"
                    leftIcon={faSliders}
                    onClick={openFilterDrawer}
                    className={styles.filterButton}
                />
            );
        }
        return null;
    };

    const handleActivateToggle = (checked: boolean) => {
        if (!hasPremiumPlan) {
            setShowPremiumAlert(true);
            return;
        }

        const updatedSettings = (marketplace.settings ?? []).map(s =>
            s.key === 'leads_enabled' ? { ...s, value: checked ? SETTINGS_TRUE_VALUE : SETTINGS_FALSE_VALUE } : s
        );

        dispatch(saveMarketplaceThunk(apiClientWrapper, { ...marketplace, settings: updatedSettings }, setTimeZone));
    };

    const activateSwitch = (
        <Switch
            checked={leadsEnabled && hasPremiumPlan}
            disabled={isSavingMarketplace}
            onChange={handleActivateToggle}
        />
    );

    const leadsTitle = (
        <span className={styles.titleWithToggle}>
            Leads
            {isMobile ? (
                <span className={`${styles.statusPill} ${leadsEnabled ? styles.statusPillOn : styles.statusPillOff}`}>
                    {leadsEnabled ? 'On' : 'Off'}
                </span>
            ) : (
                activateSwitch
            )}
        </span>
    );

    const headerActions = (
        <div className={styles.headerActions}>
            {isMobile && activateSwitch}
            {leadsEnabled && hasPremiumPlan && (
                <Button
                    id="leads_add_button"
                    label={isMobile ? 'New' : 'New Lead'}
                    leftIcon={faPlus}
                    onClick={openAddForm}
                />
            )}
        </div>
    );

    const renderContent = () => {
        // Lead Detail replaces the list in place — same route, no URL of its own.
        if (detailLead && hasPremiumPlan && leadsEnabled) {
            return (
                <LeadDetail
                    lead={detailLead}
                    onBack={closeDetail}
                />
            );
        }

        if (!hasPremiumPlan || !leadsEnabled) {
            return (
                <div className={styles.root}>
                    <div className={styles.headWrapper}>
                        <DefaultHeader
                            title={leadsTitle}
                            textBelowTitle="Track and convert leads into clients."
                            className={styles.headerContainer}
                            customButton={headerActions}
                        />
                    </div>

                    <div className={styles.activationContainer}>
                        <LeadsActivation
                            isSaving={isSavingMarketplace}
                            onActivate={() => handleActivateToggle(true)}
                        />
                    </div>
                </div>
            );
        }

        if (isFirstRender) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            );
        }

        return (
            <div className={styles.root}>
                <div className={styles.headWrapper}>
                    <DefaultHeader
                        title={leadsTitle}
                        textBelowTitle="Track and convert leads into clients."
                        className={styles.headerContainer}
                        customButton={headerActions}
                    />

                    <>
                        <SelectionBar
                            options={stageOptions}
                            selectedId={selectedStage}
                            onSelect={handleStageChange}
                            className={styles.selectionBar}
                        />

                        <div className={styles.searchAndActions}>
                            <SearchBar
                                key={searchInputKey}
                                searchText={searchText}
                                onChangeSearchText={handleSearchChange}
                                placeholder="Search by lead name"
                                className={styles.searchBarContainer}
                                rightActions={renderFilterButton()}
                            />
                        </div>
                    </>
                </div>

                <div className={styles.tableContainer}>
                    {!isGlobalEmpty && (
                        <div className={styles.tableHeader}>
                            <div className={styles.titleContainer}>
                                <h3>Total Leads ({displayedLeadsCount})</h3>
                                <span>{percentageLeads()}%</span>
                            </div>
                        </div>
                    )}
                    <LeadsTable />
                </div>
            </div>
        );
    };

    return (
        <>
            {renderContent()}

            {showPremiumAlert && (
                <PremiumFeatureAlert
                    isOpen={true}
                    onCancel={() => setShowPremiumAlert(false)}
                    text="Unlock leads with a premium plan"
                />
            )}

            {showNewBooking && (
                <CreateBookingDrawer
                    parentID={showNewBooking.clientId}
                    petID={showNewBooking.petId}
                    parentName={showNewBooking.parentName}
                    petAdded={showNewBooking.petId !== undefined}
                    showTabs={false}
                    onBooked={handleBooked}
                    onClose={closeBooking}
                    onAddPet={handleAddPet}
                />
            )}

            {addPetForClientId !== undefined &&
                createPortal(
                    <OverFullWindow onClose={handleCloseAddPet}>
                        <Client
                            hideHeader
                            isNewBooking
                            clientId={addPetForClientId}
                            onClose={handleCloseAddPet}
                            onClientSaved={() => handleCloseAddPet()}
                            onPetSaved={handlePetSaved}
                        />
                    </OverFullWindow>,
                    document.body
                )}

            {smsClient && (
                <TwoWaySMSDrawer
                    client={smsClient}
                    onClose={handleCloseSms}
                    onBack={handleCloseSms}
                />
            )}

            {showAddLead && (
                <LeadForm
                    mode={editingLead ? 'edit' : 'add'}
                    lead={editingLead || undefined}
                    onClose={closeForm}
                    onSave={saveLead}
                    isSaving={isSaving}
                />
            )}

            {showFilterLead && <LeadFiltersDrawer onClose={closeFilterDrawer} />}

            {convertingLead && (
                <ConfirmDialog
                    id="leads_convert_to_client"
                    open={true}
                    isSuccess={true}
                    title="Convert to Client"
                    question={`Convert ${
                        `${convertingLead.firstName} ${convertingLead.lastName}`.trim() || 'this lead'
                    } to a client?`}
                    subquestion="This action cannot be undone."
                    cancelButtonLabel="Cancel"
                    confirmButtonLabel="Convert"
                    processing={isConverting}
                    onCancel={closeConvert}
                    onConfirm={() => convertLead(convertingLead)}
                />
            )}

            <ShareSendPanel
                isOpen={showShareSendPanel}
                onClose={closeShareSendPanel}
                token={shareSendLead?.token}
                email={shareSendLead?.email || undefined}
                phone={shareSendLead?.phone || undefined}
            />
        </>
    );
};

// ─── Root component (wraps content in provider) ───────────────────────────────

const LeadsComponent: FunctionComponent = () => {
    return (
        <LeadsProvider>
            <LeadsContent />
        </LeadsProvider>
    );
};

export default LeadsComponent;
